import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

/**
 * Shared core for the dual-handle date slider — consumed by BOTH the
 * standalone ``DateRangeSlider`` widget (``filter_type: "date_slider"``)
 * and the slider row embedded inside the ``DateRangePicker`` popover
 * (every ``date_range`` / ``date_relative`` filter whose engine serves
 * ``bounds``). Single copy so the snapped domain construction, the
 * index math and the accessible dual-range-input pair never drift
 * between the two hosts.
 *
 * Domain: the real min/max of the filter column, served by the
 * engine's ``fetch_filter_options`` as ``bounds = [lo_iso, hi_iso]``
 * (global — never narrowed by sibling filters, so the track is
 * stable). The snap points are generated BY CONSTRUCTION from
 * ``step``:
 *
 *   day   → every day in [lo, hi]
 *   week  → the Monday of each ISO week intersecting [lo, hi] (aligned
 *           with the product's Monday-anchored ``week_iso`` columns)
 *   month → the 1st of each month intersecting [lo, hi]
 *
 * Handles move by domain INDEX (no rounding); ``lo === hi`` is allowed
 * (a one-unit window), which is why this is a bespoke pair of native
 * range inputs instead of bi-components' ``RangeSlider`` (its clamp
 * forces ``lo < hi``).
 */

export type DateSliderStep = "day" | "week" | "month";

// ── Civil-date helpers ───────────────────────────────────
// All arithmetic runs on UTC epoch-days built via ``Date.UTC`` from the
// parsed Y-M-D parts, and back through the ``getUTC*`` accessors. Never
// ``new Date("YYYY-MM-DD")`` + local accessors — for negative-offset
// timezones that shifts the civil date back one day.

const DAY_MS = 86_400_000;

function isoToUtcMs(iso: string): number | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso.trim());
  if (!m) return null;
  return Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
}

function utcMsToIso(ms: number): string {
  const d = new Date(ms);
  const y = d.getUTCFullYear();
  const mo = String(d.getUTCMonth() + 1).padStart(2, "0");
  const da = String(d.getUTCDate()).padStart(2, "0");
  return `${y}-${mo}-${da}`;
}

/** Monday of the ISO week containing ``ms`` (getUTCDay: 0 = Sunday). */
function mondayOnOrBefore(ms: number): number {
  const dow = new Date(ms).getUTCDay();
  return ms - ((dow + 6) % 7) * DAY_MS;
}

/** Build the snap-point domain (ISO days) for the given bounds + step. */
export function buildDomain(
  bounds: readonly string[] | undefined,
  step: DateSliderStep,
): string[] {
  if (!bounds || bounds.length < 2) return [];
  const lo = isoToUtcMs(bounds[0] ?? "");
  const hi = isoToUtcMs(bounds[1] ?? "");
  if (lo === null || hi === null || lo > hi) return [];
  const out: string[] = [];
  if (step === "month") {
    const start = new Date(lo);
    let y = start.getUTCFullYear();
    let m = start.getUTCMonth(); // 0-based
    let t = Date.UTC(y, m, 1);
    while (t <= hi) {
      out.push(utcMsToIso(t));
      m += 1;
      if (m > 11) {
        m = 0;
        y += 1;
      }
      t = Date.UTC(y, m, 1);
    }
    return out;
  }
  const stride = step === "week" ? 7 * DAY_MS : DAY_MS;
  // week: first point is the Monday of lo's week (== lo for Monday-
  // anchored week_iso data; at most 6 days before lo otherwise — a
  // start earlier than the data min selects nothing extra).
  let t = step === "week" ? mondayOnOrBefore(lo) : lo;
  while (t <= hi) {
    out.push(utcMsToIso(t));
    t += stride;
  }
  return out;
}

/**
 * Zoomed track bounds for the in-popover slider (UX r3).
 *
 * Model: the preset chips define the slider's ZOOM; the slider is
 * fine adjustment INSIDE that zoom. The track spans the committed
 * selection widened to DOUBLE its length (half a selection extra on
 * each side), always clamped to the column's universal ``bounds``.
 * When a clamp eats one side, the other side extends to complete the
 * doubled length (up to the bounds). "All" / no selection → the full
 * bounds (a full-bounds selection cannot be doubled).
 *
 * A one-point selection (start == end) is treated as one ``step``
 * stride so the track never degenerates to a single snap point (a
 * 0-length track has no draggable range).
 *
 * Pure function of the COMMITTED selection — hosts must NOT feed it
 * the slider's own pending window, or the track would re-anchor
 * under a live drag (the 2026-07-07 freeze family of bugs).
 */
export function expandBoundsForSelection(
  bounds: readonly string[] | undefined,
  selStartIso: string | null,
  selEndIso: string | null,
  step: DateSliderStep,
): readonly string[] | undefined {
  if (!bounds || bounds.length < 2) return bounds;
  const bLo = isoToUtcMs(bounds[0] ?? "");
  const bHi = isoToUtcMs(bounds[1] ?? "");
  if (bLo === null || bHi === null || bLo > bHi) return bounds;
  if (!selStartIso || !selEndIso) return bounds;
  let sLo = isoToUtcMs(selStartIso);
  let sHi = isoToUtcMs(selEndIso);
  if (sLo === null || sHi === null) return bounds;
  if (sLo > sHi) {
    const t = sLo;
    sLo = sHi;
    sHi = t;
  }
  // Clamp the selection itself into the universal bounds first.
  sLo = Math.max(sLo, bLo);
  sHi = Math.min(sHi, bHi);
  if (sLo > sHi) return bounds;
  // Month stride is an approximation ONLY for the padding math; the
  // actual snap points still come from buildDomain's calendar walk.
  const stride =
    step === "month" ? 30 * DAY_MS : step === "week" ? 7 * DAY_MS : DAY_MS;
  const selLen = Math.max(sHi - sLo, stride);
  const pad = selLen / 2;
  let lo = sLo - pad;
  let hi = sHi + pad;
  // Clamp; push the shortfall to the opposite side, then clamp again.
  if (lo < bLo) {
    hi += bLo - lo;
    lo = bLo;
  }
  if (hi > bHi) {
    lo -= hi - bHi;
    hi = bHi;
  }
  if (lo < bLo) lo = bLo;
  return [utcMsToIso(lo), utcMsToIso(hi)];
}

/** Index of the domain entry nearest to ``iso`` (clamped to [0, n-1]). */
export function nearestIndex(domain: readonly string[], iso: string): number {
  const target = isoToUtcMs(iso);
  if (target === null || domain.length === 0) return 0;
  let best = 0;
  let bestDelta = Number.POSITIVE_INFINITY;
  for (let i = 0; i < domain.length; i++) {
    const ms = isoToUtcMs(domain[i]!);
    if (ms === null) continue;
    const delta = Math.abs(ms - target);
    if (delta < bestDelta) {
      bestDelta = delta;
      best = i;
    }
  }
  return best;
}

// ── Controlled dual-handle slider ─────────────────────────

export interface DualDateSliderProps {
  /** Snap-point domain from :func:`buildDomain`. Must be non-empty —
   *  the host renders its own placeholder for an empty domain. */
  domain: readonly string[];
  /** Low handle index (0-based into ``domain``). */
  lo: number;
  /** High handle index. Invariant ``lo <= hi`` (equality allowed). */
  hi: number;
  /** Fires on EVERY step while dragging (already clamped so
   *  ``lo <= hi``). The host decides whether that is a live pending
   *  update (popover) or a local drag state (standalone). */
  onChange: (lo: number, hi: number) => void;
  /** Fires on pointerup / keyup / blur — the standalone widget commits
   *  its pending selection here. The popover host omits it (the
   *  "Aplicar" button is the commit gesture there). */
  onCommit?: () => void;
  /** Dark styling for the banner-embedded filter bar. The popover
   *  host stays light (popovers sit above any backdrop). */
  embedded?: boolean;
  /**
   * What the row under the track shows:
   *   "selection" — the selected ``domain[lo] → domain[hi]`` pair
   *                 (standalone widget: labels ARE the readout).
   *   "extremes"  — the domain min/max at the track ends (popover:
   *                 the selection already lives in the Inicio/Fin
   *                 pills, so the track shows its span instead).
   */
  labels?: "selection" | "extremes";
}

/**
 * Dual-handle slider over an indexed date domain.
 *
 * FREEZE FIX (semi-controlled, uncontrolled-while-dragging): the two
 * native range inputs are driven from LOCAL state while a drag is in
 * flight — the host's ``lo``/``hi`` props are only synced back into
 * the inputs when no drag is active. The original fully-controlled
 * version REVERTED the input mid-drag whenever the host echo (or the
 * ``setHi = max(next, lo)`` clamp) disagreed with the native drag
 * position; with the pointer captured, a real browser then re-emits
 * the input event for every mouse movement (~100/s), each reverted
 * render repainting the whole popover (two calendars + slider) — an
 * input/render tug-of-war that saturated the main thread and froze
 * the renderer on staging (2026-07-07). jsdom cannot reproduce it
 * (synthetic events don't re-fire on revert), which is why the unit
 * suite was green while the browser froze.
 *
 * Crossing is likewise NOT fought: dragging one handle past the other
 * is allowed in the raw local state and the rendered/emitted window is
 * simply the sorted pair — no adversarial clamp, no reverts, the host
 * contract (``lo <= hi``) still holds on every ``onChange``.
 */
export function DualDateSlider({
  domain,
  lo,
  hi,
  onChange,
  onCommit,
  embedded,
  labels = "selection",
}: DualDateSliderProps): JSX.Element {
  const { t } = useTranslation();
  const maxIdx = domain.length - 1;

  // Local mirror of the handle indices (raw = may be crossed mid-drag).
  const [local, setLocal] = useState<[number, number]>([lo, hi]);
  const dragging = useRef(false);
  useEffect(() => {
    // Calendar clicks / chips / external value changes reposition the
    // handles — but NEVER while a pointer drag owns the inputs.
    if (!dragging.current) setLocal([lo, hi]);
  }, [lo, hi]);

  const [rawLo, rawHi] = local;
  const dispLo = Math.min(rawLo, rawHi);
  const dispHi = Math.max(rawLo, rawHi);

  const move = (which: 0 | 1, next: number): void => {
    dragging.current = true;
    setLocal(which === 0 ? [next, rawHi] : [rawLo, next]);
    const a = which === 0 ? next : rawLo;
    const b = which === 1 ? next : rawHi;
    onChange(Math.min(a, b), Math.max(a, b));
  };
  const commit = (): void => {
    dragging.current = false;
    // Un-cross the raw pair on release so the inputs match what the
    // host saw (the sorted window) before the next prop sync.
    setLocal([dispLo, dispHi]);
    if (onCommit) onCommit();
  };

  const pctLo = maxIdx === 0 ? 0 : (dispLo / maxIdx) * 100;
  const pctHi = maxIdx === 0 ? 100 : (dispHi / maxIdx) * 100;

  // ── Adjacent (never overlapping) thumbs ───────────────────
  // When the two thumbs collide in pixels (same index, or values close
  // enough that the 16px balls overlap), render them ADJACENT — LO
  // nudged left, HI nudged right, edges touching — instead of stacked.
  // VISUAL offset only (≤ half a thumb each side): the value positions,
  // the filled track segment and the nearest-handle routing math all
  // keep using the un-shifted percents. Track width is observed via
  // ResizeObserver (fires on layout changes only — never per drag
  // frame, so the freeze-fix "no render per pixel" constraint holds).
  // jsdom / no-RO environments keep width 0 → no shift (legacy render).
  const trackRef = useRef<HTMLDivElement | null>(null);
  const [trackW, setTrackW] = useState(0);
  useEffect(() => {
    const el = trackRef.current;
    if (!el || typeof ResizeObserver === "undefined") return undefined;
    const ro = new ResizeObserver((entries) => {
      const w = entries[0]?.contentRect.width ?? 0;
      setTrackW((prev) => (prev === w ? prev : w));
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  const THUMB_PX = 16; // h-4 w-4 thumb
  const gapPx =
    trackW > 0 ? ((pctHi - pctLo) / 100) * trackW : Number.POSITIVE_INFINITY;
  const sepPx =
    gapPx < THUMB_PX ? Math.min((THUMB_PX - gapPx) / 2, THUMB_PX / 2) : 0;
  // When both thumbs coincide, put the LO input on top only when parked
  // in the upper half — otherwise the lower thumb could never be grabbed
  // from the domain's right edge (and vice versa).
  const loOnTop = rawLo === rawHi && rawLo > maxIdx / 2;

  // ── Nearest-handle pointer routing ────────────────────────
  // The two native inputs overlap full-width, so only the TOP one
  // (higher z-index) receives pointer events. Pre-fix the HI input sat
  // on top in every state except the collapsed-upper-half ``loOnTop``
  // case, which made the LO thumb un-grabbable. Now the track wrapper
  // follows the pointer's X and raises the input whose thumb is nearest.
  //
  // Freeze-fix constraint (2026-07-07): NO render per pointer movement.
  // ``nearest`` has only two meaningful values and is updated through a
  // ref guard, so state changes exactly when the nearest handle FLIPS
  // (≤ a couple of times per gesture), never per pixel.
  //
  // Touch limitation: without hover there is no pointermove before the
  // tap, and by the time ``onPointerDownCapture`` runs the browser has
  // already hit-tested the event against the PREVIOUS z-order — so the
  // first touch drag may grab the fallback handle. The capture handler
  // still records the tap position, leaving the correct z-order for the
  // next gesture. (Keyboard is unaffected: focus ignores z-index.)
  const [nearest, setNearest] = useState<0 | 1 | null>(null);
  const nearestRef = useRef<0 | 1 | null>(null);
  const updateNearest = (e: React.PointerEvent<HTMLDivElement>): void => {
    const rect = e.currentTarget.getBoundingClientRect();
    if (rect.width <= 0) return; // jsdom / display:none — keep fallback
    const pct = ((e.clientX - rect.left) / rect.width) * 100;
    const dLo = Math.abs(pct - pctLo);
    const dHi = Math.abs(pct - pctHi);
    let next: 0 | 1;
    if (dLo < dHi) next = 0;
    else if (dHi < dLo) next = 1;
    // Tie (coincident thumbs / equidistant pointer). With the adjacent
    // rendering LO always sits visually LEFT of HI, so a pointer left
    // of the shared position means the user is grabbing the left ball
    // (LO) and vice versa. Dead-center falls back to the handle that
    // can WIDEN the window — thumbs in the upper half have more room
    // to the left (LO), lower half more room to the right (HI).
    else if (pct < pctLo) next = 0;
    else if (pct > pctHi) next = 1;
    else next = (pctLo + pctHi) / 2 > 50 ? 0 : 1;
    if (nearestRef.current !== next) {
      nearestRef.current = next;
      setNearest(next);
    }
  };
  const clearNearest = (): void => {
    if (nearestRef.current !== null) {
      nearestRef.current = null;
      setNearest(null);
    }
  };
  // Hover-tracked nearest handle wins; without hover (initial render /
  // touch first contact) fall back to the collapsed-thumbs heuristic.
  const topInput: 0 | 1 = nearest ?? (loOnTop ? 0 : 1);

  const inputClass = "absolute top-0 h-6 w-full cursor-pointer opacity-0";
  const thumbClass = `absolute top-0.5 h-4 w-4 rounded-full border-2 shadow-sm transition-transform motion-safe:hover:scale-110 ${
    embedded ? "border-white bg-white/90" : "border-primary bg-white"
  }`;

  return (
    <div className="w-full">
      <div
        ref={trackRef}
        className="relative h-6"
        onPointerMove={updateNearest}
        onPointerDownCapture={updateNearest}
        onPointerLeave={clearNearest}
      >
        <div
          className={`absolute left-0 right-0 top-2 h-1.5 rounded-full ${
            embedded ? "bg-white/20" : "bg-neutral-100"
          }`}
        />
        <div
          className={`absolute top-2 h-1.5 rounded-full ${
            embedded ? "bg-white/80" : "bg-primary"
          }`}
          style={{ left: `${pctLo}%`, width: `${pctHi - pctLo}%` }}
        />
        <input
          type="range"
          min={0}
          max={maxIdx}
          step={1}
          value={rawLo}
          aria-label={t("dateslider.from")}
          aria-valuetext={domain[dispLo]}
          onChange={(e) => move(0, Number(e.target.value))}
          onPointerUp={commit}
          onKeyUp={commit}
          onBlur={commit}
          className={inputClass}
          style={{ zIndex: topInput === 0 ? 4 : 3 }}
        />
        <input
          type="range"
          min={0}
          max={maxIdx}
          step={1}
          value={rawHi}
          aria-label={t("dateslider.to")}
          aria-valuetext={domain[dispHi]}
          onChange={(e) => move(1, Number(e.target.value))}
          onPointerUp={commit}
          onKeyUp={commit}
          onBlur={commit}
          className={inputClass}
          style={{ zIndex: topInput === 1 ? 4 : 3 }}
        />
        <div
          data-thumb="lo"
          className={`${thumbClass} pointer-events-none`}
          style={{ left: `calc(${pctLo}% - ${8 + sepPx}px)` }}
        />
        <div
          data-thumb="hi"
          className={`${thumbClass} pointer-events-none`}
          style={{ left: `calc(${pctHi}% - ${8 - sepPx}px)` }}
        />
      </div>
      {labels === "selection" ? (
        /* Handle date labels under the thumbs; read together with the
           separator they double as the chosen range in text form. */
        <div className="mt-1 flex items-center justify-between gap-1">
          <span
            title={t("dateslider.from")}
            className={`text-2xs font-semibold tabular-nums ${
              embedded ? "text-white/80" : "text-primary"
            }`}
          >
            {domain[dispLo]}
          </span>
          <span
            className={`text-2xs ${
              embedded ? "text-white/50" : "text-neutral-400"
            }`}
          >
            →
          </span>
          <span
            title={t("dateslider.to")}
            className={`text-2xs font-semibold tabular-nums ${
              embedded ? "text-white/80" : "text-primary"
            }`}
          >
            {domain[dispHi]}
          </span>
        </div>
      ) : (
        /* Domain extremes at the track ends — the selection readout
           lives elsewhere (the popover's Inicio/Fin pills). */
        <div className="mt-1 flex items-center justify-between gap-1">
          <span
            className={`text-2xs font-medium tabular-nums ${
              embedded ? "text-white/75" : "text-neutral-600"
            }`}
          >
            {domain[0]}
          </span>
          <span
            className={`text-2xs font-medium tabular-nums ${
              embedded ? "text-white/75" : "text-neutral-600"
            }`}
          >
            {domain[maxIdx]}
          </span>
        </div>
      )}
    </div>
  );
}
